<template>
  <div class="drawing-board-wrapper">
    <!-- Toolbar -->
    <div class="db-toolbar">
      <div class="db-tools-left">
        <button
          v-for="tool in tools"
          :key="tool.id"
          :class="['db-tool-btn', { active: currentTool === tool.id }]"
          @click="selectTool(tool.id)"
          :title="tool.label"
        >
          <el-icon :size="18">
            <component :is="tool.icon" />
          </el-icon>
        </button>
        <div class="db-divider"></div>
        <div class="db-size-control">
          <span class="db-size-label">{{ brushSize }}px</span>
          <input
            type="range"
            v-model="brushSize"
            min="2"
            max="30"
            class="db-size-slider"
          />
        </div>
        <div class="db-color-picker">
          <el-color-picker v-model="brushColor" size="small" :predefine="quickColors" />
        </div>
      </div>
      <div class="db-tools-right">
        <button class="db-tool-btn" @click="undo" :disabled="lines.length === 0" :title="$t('Undo')">
          <el-icon :size="18"><RefreshLeft /></el-icon>
        </button>
        <button class="db-tool-btn" @click="clearAll" :disabled="lines.length === 0" :title="$t('Clear')">
          <el-icon :size="18"><Delete /></el-icon>
        </button>
      </div>
    </div>

    <!-- Canvas -->
    <div class="db-canvas-container" ref="canvasContainer">
      <canvas
        ref="drawCanvas"
        class="db-canvas"
        @mousedown="startDrawing"
        @mousemove="draw"
        @mouseup="stopDrawing"
        @mouseleave="stopDrawing"
        @touchstart.prevent="startDrawingTouch"
        @touchmove.prevent="drawTouch"
        @touchend.prevent="stopDrawing"
      ></canvas>
      <div v-if="lines.length === 0" class="db-canvas-hint">
        {{ $t('DrawHint') }}
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="db-footer">
      <el-button @click="$emit('cancel')">{{ $t('Cancel') }}</el-button>
      <el-button type="primary" @click="confirm">{{ $t('Apply Mask') }}</el-button>
    </div>
  </div>
</template>

<script>
import { ElButton, ElIcon, ElColorPicker } from 'element-plus';
import { EditPen, RefreshLeft, Delete } from '@element-plus/icons-vue';

export default {
  name: 'DrawingBoard',
  components: {
    ElButton, ElIcon, ElColorPicker,
    EditPen, RefreshLeft, Delete
  },
  emits: ['confirm', 'cancel'],
  data() {
    return {
      currentTool: 'pen',
      brushSize: 8,
      brushColor: '#000000',
      lines: [],
      currentLine: null,
      isDrawing: false,
      canvasCtx: null,
      quickColors: ['#000000', '#ffffff', '#ff0000', '#4f46e5', '#10b981', '#f59e0b']
    };
  },
  computed: {
    tools() {
      return [
        { id: 'pen', label: this.$t('Pen'), icon: 'EditPen' },
        { id: 'eraser', label: this.$t('Eraser'), icon: 'Remove' }
      ];
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCanvas();
    });
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    initCanvas() {
      const canvas = this.$refs.drawCanvas;
      const container = this.$refs.canvasContainer;
      if (!canvas || !container) return;

      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';

      this.canvasCtx = canvas.getContext('2d');
      this.canvasCtx.scale(2, 2);
      this.canvasCtx.lineCap = 'round';
      this.canvasCtx.lineJoin = 'round';

      // White background
      this.canvasCtx.fillStyle = '#ffffff';
      this.canvasCtx.fillRect(0, 0, rect.width, rect.height);

      this.redrawAll();
    },

    handleResize() {
      // Save current state
      const imageData = this.canvasCtx ? this.canvasCtx.getImageData(0, 0,
        this.$refs.drawCanvas.width, this.$refs.drawCanvas.height) : null;

      this.initCanvas();

      // Restore
      if (imageData && this.canvasCtx) {
        this.canvasCtx.putImageData(imageData, 0, 0);
      }
    },

    selectTool(toolId) {
      this.currentTool = toolId;
    },

    getPos(e) {
      const canvas = this.$refs.drawCanvas;
      const rect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    },

    getTouchPos(e) {
      const canvas = this.$refs.drawCanvas;
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    },

    startDrawing(e) {
      this.isDrawing = true;
      const pos = this.getPos(e);
      this.currentLine = {
        tool: this.currentTool,
        size: this.brushSize,
        color: this.brushColor,
        points: [pos]
      };
    },

    startDrawingTouch(e) {
      this.isDrawing = true;
      const pos = this.getTouchPos(e);
      this.currentLine = {
        tool: this.currentTool,
        size: this.brushSize,
        color: this.brushColor,
        points: [pos]
      };
    },

    draw(e) {
      if (!this.isDrawing || !this.currentLine) return;
      const pos = this.getPos(e);
      this.currentLine.points.push(pos);
      this.drawLineToCanvas(this.currentLine);
    },

    drawTouch(e) {
      if (!this.isDrawing || !this.currentLine) return;
      const pos = this.getTouchPos(e);
      this.currentLine.points.push(pos);
      this.drawLineToCanvas(this.currentLine);
    },

    stopDrawing() {
      if (!this.isDrawing) return;
      this.isDrawing = false;
      if (this.currentLine && this.currentLine.points.length > 0) {
        this.lines.push(this.currentLine);
      }
      this.currentLine = null;
    },

    drawLineToCanvas(line) {
      if (!this.canvasCtx || line.points.length < 2) return;

      const ctx = this.canvasCtx;
      ctx.beginPath();
      ctx.strokeStyle = line.tool === 'eraser' ? '#ffffff' : line.color;
      ctx.lineWidth = line.tool === 'eraser' ? line.size * 2 : line.size;

      const pts = line.points;
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(pts[i].x, pts[i].y);
      }
      ctx.stroke();
    },

    redrawAll() {
      if (!this.canvasCtx) return;
      const ctx = this.canvasCtx;
      const w = this.$refs.drawCanvas.width / 2;
      const h = this.$refs.drawCanvas.height / 2;

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);

      for (const line of this.lines) {
        this.drawLineToCanvas(line);
      }
    },

    undo() {
      this.lines.pop();
      this.redrawAll();
    },

    clearAll() {
      this.lines = [];
      this.redrawAll();
    },

    confirm() {
      const canvas = this.$refs.drawCanvas;
      if (!canvas) return;
      // Export as base64 PNG — white pixels become transparent
      const dataUrl = canvas.toDataURL('image/png');
      this.$emit('confirm', dataUrl);
    }
  }
};
</script>

<style scoped>
.drawing-board-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 400px;
  background: #f8fafc;
  border-radius: 8px;
  overflow: hidden;
}

.db-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 8px;
}

.db-tools-left,
.db-tools-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.db-tool-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.15s ease;
}

.db-tool-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.db-tool-btn.active {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
}

.db-tool-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.db-divider {
  width: 1px;
  height: 24px;
  background: #e5e7eb;
  margin: 0 6px;
}

.db-size-control {
  display: flex;
  align-items: center;
  gap: 6px;
}

.db-size-label {
  font-size: 11px;
  color: #6b7280;
  width: 28px;
  text-align: right;
  font-family: monospace;
}

.db-size-slider {
  width: 80px;
  height: 4px;
  accent-color: #4f46e5;
  cursor: pointer;
}

.db-color-picker {
  margin-left: 4px;
}

.db-canvas-container {
  flex: 1;
  position: relative;
  margin: 8px;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  border: 2px dashed #d1d5db;
  min-height: 250px;
}

.db-canvas {
  display: block;
  width: 100%;
  height: 100%;
  cursor: crosshair;
  position: absolute;
  top: 0;
  left: 0;
}

.db-canvas-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #9ca3af;
  font-size: 16px;
  pointer-events: none;
  text-align: center;
}

.db-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
}
</style>
